(() => {
  "use strict";

  /* =====================================================
     DASH & TWINE BLOG ANALYTICS
  ===================================================== */

  const API_BASE_URL =
    window.DT_BLOG_ANALYTICS_API ||
    "https://dash-twine-blog-analytics.onrender.com/api/analytics";

  const MAX_POSTS = 5;

  const articleData = document.querySelector("#dt-blog-analytics");

  if (!articleData) return;

  const articleId = articleData.dataset.articleId;
  const articleTitle = articleData.dataset.articleTitle;
  const articleUrl = articleData.dataset.articleUrl;
  const articleImage = articleData.dataset.articleImage || "";

  const sidebar = document.querySelector(".blog-sidebar-content");

  /* =====================================================
     VALIDATE CURRENT ARTICLE URL
  ===================================================== */

  let parsedArticleUrl;

  try {
    parsedArticleUrl = new URL(articleUrl);

    if (
      parsedArticleUrl.origin !== window.location.origin ||
      !parsedArticleUrl.pathname.includes("/blogs/")
    ) {
      throw new Error("Unexpected article URL.");
    }
  } catch (error) {
    console.error("Invalid article URL:", error.message);
    return;
  }

  /* =====================================================
     TRACK ARTICLE VIEW
  ===================================================== */

  async function trackArticleView() {
    try {
      const sessionKey = `dt_blog_viewed_${articleId}`;

      if (sessionStorage.getItem(sessionKey) === "1") {
        console.log("Blog view already tracked in this session.");
        return;
      }

      const response = await fetch(`${API_BASE_URL}/view`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          shopifyArticleId: articleId,
          title: articleTitle,
          url: parsedArticleUrl.href,
          image: articleImage.startsWith("//")
            ? `https:${articleImage}`
            : articleImage
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(
          result.message || "View tracking failed."
        );
      }

      sessionStorage.setItem(sessionKey, "1");

      console.log("Blog view tracked:", result);
    } catch (error) {
      console.error("Blog analytics tracking error:", error.message);
    }
  }

  /* =====================================================
     CREATE SIDEBAR SECTION
  ===================================================== */

  function createSidebarSection(title, sectionId) {
    if (!sidebar) return null;

    const existing = document.getElementById(sectionId);

    if (existing) return existing;

    const section = document.createElement("div");

    section.id = sectionId;
    section.className =
      "blog-post recent-post-sidebar dt-analytics-sidebar";

    const headingWrapper = document.createElement("div");
    headingWrapper.className = "blog-heading-wrapper";

    const heading = document.createElement("h2");

    const articleContent = document.querySelector(".article_content");
    const headingColor = articleContent?.className.match(
      /heading-color-([\w-]+)/
    )?.[1] || "";

    heading.className =
      "heading inline-richtext color-" + headingColor;

    heading.textContent = title;
    headingWrapper.appendChild(heading);

    const list = document.createElement("ul");
    list.className = "recent-posts-list";

    const status = document.createElement("p");
    status.className = "dt-analytics-status";
    status.textContent = "Loading posts...";

    section.appendChild(headingWrapper);
    section.appendChild(status);
    section.appendChild(list);

    sidebar.appendChild(section);

    return section;
  }

  /* =====================================================
     GET IMAGE URL
  ===================================================== */

  function getImageUrl(item) {
    const candidates = [
      item.image,
      item.imageUrl,
      item.thumbnailUrl,
      item.thumbnail,
      item.featuredImage,
      item.featured_image,
    ];

    for (const candidate of candidates) {
      const value =
        typeof candidate === "string"
          ? candidate
          : candidate?.url || candidate?.src || "";

      if (!value) continue;

      try {
        const url = new URL(value, window.location.origin);

        if (
          url.protocol === "https:" &&
          [
            "cdn.shopify.com",
            "dashandtwine.com",
            "www.dashandtwine.com",
          ].includes(url.hostname)
        ) {
          return url.href;
        }
      } catch {
        // Try the next image field.
      }
    }

    return "";
  }

  /* =====================================================
     RENDER ARTICLES WITH THUMBNAILS
  ===================================================== */

  function renderArticles(section, articles, type) {
    if (!section) return;

    const list = section.querySelector(".recent-posts-list");
    const status = section.querySelector(".dt-analytics-status");

    if (!list || !status) return;

    list.replaceChildren();

    const validArticles = (
      Array.isArray(articles) ? articles : []
    )
      .filter(item => item && item.title && item.url)
      .slice(0, MAX_POSTS);

    if (validArticles.length === 0) {
      status.textContent = "No posts available.";
      status.style.display = "block";
      return;
    }

    status.style.display = "none";

    validArticles.forEach(item => {
      const li = document.createElement("li");
      li.className = "recent-post-item dt-analytics-post-item";

      const link = document.createElement("a");
      link.className = "dt-analytics-post-link";

      try {
        const url = new URL(item.url, window.location.origin);

        if (
          url.protocol !== "https:" ||
          !["dashandtwine.com", "www.dashandtwine.com"].includes(
            url.hostname
          ) ||
          !url.pathname.includes("/blogs/")
        ) {
          return;
        }

        link.href = url.href;
      } catch {
        return;
      }

      const imageUrl = getImageUrl(item);

      if (imageUrl) {
        const img = document.createElement("img");

        img.className = "dt-analytics-thumbnail";
        img.src = imageUrl;
        img.alt = item.title;
        img.loading = "lazy";
        img.width = 100;
        img.height = 75;

        img.onerror = () => {
          img.remove();
        };

        link.appendChild(img);
      }

      const content = document.createElement("div");
      content.className = "dt-analytics-post-content";

      const title = document.createElement("span");
      title.className = "dt-analytics-post-title";
      title.textContent = item.title;

      content.appendChild(title);

      if (type === "most-viewed") {
        const views = document.createElement("span");
        views.className = "dt-blog-view-count";

        const count = Math.max(
          0,
          Number(item.views ?? item.totalViews) || 0
        );

        views.textContent =
          `${count} ${count === 1 ? "view" : "views"}`;

        content.appendChild(views);
      }

      link.appendChild(content);
      li.appendChild(link);
      list.appendChild(li);
    });

    console.log(`${type} rendered:`, validArticles);
  }

  /* =====================================================
     FETCH ANALYTICS API
  ===================================================== */

  async function fetchArticles(endpoint) {
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(
        `${endpoint} API failed: HTTP ${response.status}`
      );
    }

    const result = await response.json();

    if (!result.success || !Array.isArray(result.articles)) {
      throw new Error(`${endpoint} returned an invalid response.`);
    }

    return result.articles;
  }

  async function loadSidebarSection(
    endpoint,
    title,
    sectionId,
    type
  ) {
    const section = createSidebarSection(title, sectionId);

    if (!section) return;

    try {
      const articles = await fetchArticles(endpoint);

      renderArticles(section, articles, type);

      console.log(`${title} loaded:`, articles);
    } catch (error) {
      const status = section.querySelector(".dt-analytics-status");

      if (status) {
        status.textContent = "Posts are temporarily unavailable.";
        status.style.display = "block";
      }

      console.error(`${title} error:`, error.message);
    }
  }

  /* =====================================================
     INITIALIZE
  ===================================================== */

  trackArticleView();

  if (sidebar) {
    loadSidebarSection(
      "trending",
      "Trending Posts",
      "dt-trending-posts",
      "trending"
    );

    loadSidebarSection(
      "most-viewed",
      "Most Viewed Posts",
      "dt-most-viewed-posts",
      "most-viewed"
    );
  }
})();
